import React, { useState } from 'react';
import { 
  HelpCircle, 
  UserPlus, 
  MessageSquare, 
  CreditCard, 
  Headphones, 
  ChevronRight, 
  Copy, 
  Check, 
  ShieldCheck, 
  FileText, 
  Calendar,
  Lock,
  ExternalLink,
  PhoneCall
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { showToast } from './Toast';

export default function PlatformGuideSection({ onOpenPayment = null }) {
  const { currentLang, t } = useLanguage();
  const [copiedCard, setCopiedCard] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const cardPan = '5614 6814 2744 1112';

  const handleCopyCard = () => {
    navigator.clipboard.writeText(cardPan.replace(/\s+/g, ''));
    setCopiedCard(true);
    showToast(
      currentLang === 'en' 
        ? 'Card number copied to clipboard: 5614 6814 2744 1112' 
        : currentLang === 'ru' 
        ? 'Номер карты скопирован: 5614 6814 2744 1112' 
        : 'Karta raqami nusxalandi: 5614 6814 2744 1112',
      'success'
    );
    setTimeout(() => setCopiedCard(false), 2500);
  };

  const guides = [
    {
      id: 'reg-booking',
      icon: UserPlus,
      title: {
        uz: '1. Ro‘yxatdan o‘tish va Qabulga yozilish',
        ru: '1. Регистрация и Запись на прием',
        en: '1. Registration & Appointment Booking',
      },
      badge: {
        uz: 'Qulay & Tezkor',
        ru: 'Быстро и Удобно',
        en: 'Fast & Easy',
      },
      content: {
        uz: [
          'Bosh sahifadagi «Qabulga yozilish» tugmasini bosing va kerakli yuridik amaliyot yo‘nalishini hamda advokatni tanlang.',
          'Telefon raqamingiz va qisqacha murojaat sababini kiriting. Tizim sizga maxsus bron kodini (masalan: APT-2026-000001) beradi.',
          'Uchrashuv tasdiqlanganda telefoningizga SMS-xabar va shaxsiy kabinetingizga bildirishnoma yuboriladi.',
        ],
        ru: [
          'Нажмите кнопку «Запись на прием» на главной странице, выберите нужное юридическое направление и адвоката.',
          'Укажите номер телефона и краткую причину обращения. Система сгенерирует персональный код брони (например: APT-2026-000001).',
          'После подтверждения приема на ваш телефон поступит SMS-уведомление, а также оповещение в личный кабинет.',
        ],
        en: [
          'Click the "Book Appointment" button on the homepage, select your legal area of interest and designated attorney.',
          'Provide your phone number and brief matter summary. The platform issues a unique booking reference (e.g., APT-2026-000001).',
          'Once confirmed by counsel, an instant SMS confirmation and in-platform notification will be dispatched.',
        ],
      },
    },
    {
      id: 'chat-files',
      icon: MessageSquare,
      title: {
        uz: '2. Shaxsiy Chat va Hujjat (PDF/Rasm) almashish',
        ru: '2. Личный Чат и Обмен документами (PDF/Фото)',
        en: '2. Confidential Chat & Document Sharing',
      },
      badge: {
        uz: '100% Maxfiy',
        ru: 'Конфиденциально',
        en: '100% Confidential',
      },
      content: {
        uz: [
          'Har bir mijoz va advokat o‘rtasida xavfsiz izolyatsiyalangan alohida shaxsiy xona faoliyat yuritadi. Yozishmalar hech qachon begonalarga ko‘rinmaydi.',
          'Chat orqali tergov, sud yoki shartnoma hujjatlarini PDF va rasm (JPG, PNG) formatlarida xavfsiz yuborishingiz mumkin.',
          'Xavfsizlik talablariga muvofiq: APK va begona dastur fayllari tizimga qat’iyan yuklanmaydi va qabul qilinmaydi.',
        ],
        ru: [
          'Между каждым клиентом и адвокатом действует изолированная защищенная комната переписки. Доступ третьих лиц полностью исключен.',
          'Через чат вы можете отправлять следственные, судебные и договорные документы в форматах PDF и фото (JPG, PNG).',
          'В целях кибербезопасности: исполняемые файлы (APK, EXE) строго заблокированы на уровне сервера.',
        ],
        en: [
          'Each client and attorney communicates inside an isolated, encrypted chat room protected under attorney-client privilege.',
          'Share procedural court documents, contracts, and evidence securely in PDF and image (JPG, PNG) formats.',
          'Strict security policy: executable files (.apk, .exe) are strictly prohibited and automatically blocked.',
        ],
      },
    },
    {
      id: 'subscription-payment',
      icon: CreditCard,
      title: {
        uz: '3. Oylik To‘lov va Muddatni uzaytirish',
        ru: '3. Ежемесячная Оплата и Продление подписки',
        en: '3. Monthly Subscription & Renewals',
      },
      badge: {
        uz: 'Shaffof Tariflar',
        ru: 'Прозрачные тарифы',
        en: 'Clear Pricing',
      },
      content: {
        uz: [
          'Fuqarolar (mijozlar) uchun platformadan to‘liq foydalanish va shaxsiy kabinet: oyiga 35 000 so‘m.',
          'Advokatlar uchun kasbiy kabinet va mijozlar bilan ishlash: oyiga 99 000 so‘m.',
          'To‘lov rasmiy karta orqali amalga oshiriladi: 5614 6814 2744 1112 (Click va Payme orqali).',
          'Oylik muddat tugaganda ma’lumotlaringiz, hujjatlaringiz va chatlaringiz o‘chib ketmaydi. To‘lov qilingach darhol davom etasiz.',
        ],
        ru: [
          'Для граждан (клиентов) доступ к личному кабинету и онлайн-консультациям: ровно 35 000 сум в месяц.',
          'Для адвокатов доступ к рабочему кабинету и взаимодействию с доверителями: ровно 99 000 сум в месяц.',
          'Оплата принимается на официальную карту: 5614 6814 2744 1112 (через Click и Payme).',
          'По истечении месяца ваши документы и переписка сохраняются в полной безопасности. После оплаты доступ возобновляется.',
        ],
        en: [
          'For clients: full access to digital counsel workspace & chat: strictly 35,000 UZS/month.',
          'For attorneys: practice management workspace & client portal: strictly 99,000 UZS/month.',
          'Payments routed via official card: 5614 6814 2744 1112 (supported via Click and Payme).',
          'Data preservation: your documents and chat history are never deleted when subscriptions expire; renew anytime.',
        ],
      },
    },
    {
      id: 'support-admin',
      icon: Headphones,
      title: {
        uz: '4. Ma’muriyat va Texnik yordam bilan bog‘lanish',
        ru: '4. Администрация и Служба технической поддержки',
        en: '4. Administration & Technical Support',
      },
      badge: {
        uz: '24/7 Aloqa',
        ru: 'Поддержка 24/7',
        en: '24/7 Support',
      },
      content: {
        uz: [
          'To‘lov bo‘yicha chek yuborish, akkauntga bepul ruxsat so‘rash yoki texnik savollar bo‘yicha ma’muriyat bilan to‘g‘ridan-to‘g‘ri bog‘lanishingiz mumkin.',
          'Rasmiy aloqa telefoni: +998 (97) 325-07-70 | Bosh ofis: Jizzax shahar, Turon Bank ro‘parasi.',
          'Admin panel orqali ma’muriyat zarur hollarda fuqarolar va advokatlarga bepul cheksiz kirish huquqini taqdim etishi mumkin.',
        ],
        ru: [
          'По вопросам подтверждения платежей, предоставления бесплатного доступа или технической помощи свяжитесь с администрацией.',
          'Официальный телефон: +998 (97) 325-07-70 | Главный офис: г. Джизак, напротив Турон Банка.',
          'Администрация платформы оставляет за собой право предоставлять бессрочный бесплатный доступ по запросу.',
        ],
        en: [
          'For receipt verification, fee waivers, or technical inquiries, contact our platform administration directly.',
          'Direct contact helpline: +998 (97) 325-07-70 | Headquarters: Jizzakh city, opposite Turon Bank.',
          'Administrative leadership can grant permanent fee exemptions and courtesy access through the admin console.',
        ],
      },
    },
  ];

  return (
    <section id="guide" className="relative py-16 sm:py-20 bg-[#070b13] border-t border-[#141d2d] text-slate-200 overflow-hidden">
      {/* Background Subtle Ambience */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#c59b27]/3 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-blue-500/3 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#101726] border border-[#c59b27]/30 text-[#c59b27] text-xs font-semibold uppercase tracking-wider mb-3">
            <HelpCircle className="w-3.5 h-3.5 mr-1.5" />
            <span>
              {currentLang === 'en' ? 'Platform Knowledge Base' : currentLang === 'ru' ? 'Руководство и Поддержка' : 'Texnik Yordam & Qo‘llanma'}
            </span>
          </div>

          <h2 className="font-serif text-2xl sm:text-4xl font-bold text-slate-100 tracking-tight">
            {currentLang === 'en' 
              ? 'Technical Support & Platform Usage Guide' 
              : currentLang === 'ru' 
              ? 'Техническая помощь и Руководство пользователя' 
              : 'Texnik Yordam va Saytdan Foydalanish Bo‘yicha Qo‘llanma'}
          </h2>

          <p className="text-xs sm:text-sm text-slate-400 font-light mt-3 leading-relaxed">
            {currentLang === 'en'
              ? 'Clear, practical guidelines on consultation booking, secure attorney chat, subscriptions, and administrative support.'
              : currentLang === 'ru'
              ? 'Понятные инструкции по записи на прием, конфиденциальному чату с адвокатом, оплате и связи со службой поддержки.'
              : 'Qabulga yozilish, advokat bilan xavfsiz shaxsiy chat, oylik to‘lovlar va ma’muriyat bilan bog‘lanish bo‘yicha amaliy qo‘llanma.'}
          </p>
        </div>

        {/* Official Card Quick Action Banner */}
        <div className="mb-10 p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-[#0a101d] via-[#0d1627] to-[#0a101d] border border-[#c59b27]/40 shadow-xl flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-3.5">
            <div className="w-12 h-12 rounded-xl bg-[#142034] border border-[#c59b27]/50 flex items-center justify-center text-[#c59b27] shrink-0 shadow-md">
              <CreditCard className="w-6 h-6" />
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider text-[#c59b27] font-semibold block">
                {currentLang === 'en' ? 'Official Payment Account' : currentLang === 'ru' ? 'Официальный счет для оплаты' : 'Rasmiy To‘lov Karta Raqami'}
              </span>
              <div className="flex items-center space-x-2 mt-0.5">
                <span className="font-mono text-base sm:text-lg font-bold text-slate-100 tracking-wider">
                  {cardPan}
                </span>
                <span className="text-[11px] text-slate-400 font-medium">
                  (JIZZAX ADVOKATLARI)
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3 w-full md:w-auto">
            <button
              type="button"
              onClick={handleCopyCard}
              className="flex-1 md:flex-none px-4 py-2 rounded-lg bg-[#142034] hover:bg-[#1b2b46] border border-[#c59b27]/50 hover:border-[#c59b27] text-xs font-semibold text-slate-200 hover:text-white transition-all flex items-center justify-center cursor-pointer shadow-xs"
            >
              {copiedCard ? (
                <>
                  <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-400" />
                  <span className="text-emerald-400">{currentLang === 'en' ? 'Copied' : currentLang === 'ru' ? 'Скопировано' : 'Nusxalandi!'}</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5 mr-1.5 text-[#c59b27]" />
                  <span>{currentLang === 'en' ? 'Copy Card' : currentLang === 'ru' ? 'Скопировать карту' : 'Kartani nusxalash'}</span>
                </>
              )}
            </button>

            {onOpenPayment && (
              <button
                type="button"
                onClick={onOpenPayment}
                className="flex-1 md:flex-none px-4 py-2 rounded-lg bg-[#c59b27] hover:bg-[#d4af37] text-slate-950 text-xs font-bold transition-all flex items-center justify-center cursor-pointer shadow-md"
              >
                <span>{currentLang === 'en' ? 'Pay Online (Click / Payme)' : currentLang === 'ru' ? 'Оплатить онлайн' : 'To‘lov qilish'}</span>
                <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </button>
            )}
          </div>
        </div>

        {/* 4 Interactive Guides Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          {guides.map((item, idx) => {
            const Icon = item.icon;
            const isSelected = activeTab === idx;

            return (
              <div
                key={item.id}
                onClick={() => setActiveTab(idx)}
                className={`group relative overflow-hidden rounded-2xl p-6 sm:p-7 transition-all duration-300 ease-out border flex flex-col justify-between cursor-pointer hover:-translate-y-2 hover:shadow-[0_20px_45px_rgba(0,0,0,0.85),0_0_35px_rgba(197,155,39,0.18)] ${
                  isSelected
                    ? 'bg-[#0a1120] border-[#c59b27] shadow-2xl shadow-black/90 ring-1 ring-[#c59b27]/40'
                    : 'bg-[#090e18] border-[#162238] hover:border-[#c59b27]/70 hover:bg-[#0c1424]'
                }`}
              >
                {/* Top Animated Gold Glow Accent Bar */}
                <div 
                  className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c59b27] to-transparent transition-all duration-500 ${
                    isSelected 
                      ? 'opacity-100 scale-x-100' 
                      : 'opacity-0 scale-x-50 group-hover:opacity-100 group-hover:scale-x-100'
                  }`} 
                />

                {/* Subtle Ambient Hover Glow */}
                <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-[#c59b27]/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                <div className="relative z-10">
                  {/* Card Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 ${
                        isSelected 
                          ? 'bg-[#c59b27]/20 border border-[#c59b27] text-[#c59b27] shadow-[0_0_15px_rgba(197,155,39,0.25)]' 
                          : 'bg-[#0f1728] border border-[#1b2b46] text-slate-400 group-hover:border-[#c59b27] group-hover:text-[#c59b27] group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(197,155,39,0.35)]'
                      }`}>
                        <Icon className="w-5 h-5 transition-transform duration-300 group-hover:scale-105" />
                      </div>
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded transition-all duration-300 ${
                        isSelected
                          ? 'bg-[#c59b27]/20 text-[#c59b27] border border-[#c59b27]/40 shadow-xs'
                          : 'text-[#c59b27] bg-[#c59b27]/10 border border-[#c59b27]/25 group-hover:bg-[#c59b27]/25 group-hover:border-[#c59b27]/50'
                      }`}>
                        {item.badge[currentLang] || item.badge.uz}
                      </span>
                    </div>

                    <div className="text-[11px] font-mono text-slate-500 group-hover:text-[#c59b27] transition-colors duration-300 font-bold">
                      0{idx + 1}
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="font-serif text-base sm:text-lg font-bold text-slate-100 group-hover:text-white transition-colors duration-200 mb-3.5">
                    {item.title[currentLang] || item.title.uz}
                  </h3>

                  {/* Points list */}
                  <div className="space-y-2.5 text-xs text-slate-300 font-light leading-relaxed">
                    {(item.content[currentLang] || item.content.uz).map((point, pIdx) => (
                      <div key={pIdx} className="flex items-start space-x-2.5">
                        <div className="w-4 h-4 rounded-full bg-[#121c2e] border border-[#c59b27]/40 group-hover:border-[#c59b27] group-hover:shadow-[0_0_8px_rgba(197,155,39,0.3)] flex items-center justify-center text-[#c59b27] shrink-0 mt-0.5 transition-all duration-300">
                          <Check className="w-2.5 h-2.5" />
                        </div>
                        <span className="text-slate-300 group-hover:text-slate-200 transition-colors duration-200">{point}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Subfooter */}
                <div className="relative z-10 pt-4 mt-4 border-t border-[#141e30] group-hover:border-[#1e2f4c] flex items-center justify-between text-[11px] text-slate-400 transition-colors duration-300">
                  <span className="flex items-center text-[#c59b27] group-hover:text-[#e4be58] transition-colors">
                    <ShieldCheck className="w-3.5 h-3.5 mr-1" />
                    {currentLang === 'en' ? 'Verified guideline' : currentLang === 'ru' ? 'Проверенная инструкция' : 'Rasmiy kafolatlangan tartib'}
                  </span>
                  <span className="text-slate-500 group-hover:text-slate-400 font-mono transition-colors">
                    Jizzax Advokatlari
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Need Urgent Assistance Direct Helpline */}
        <div className="mt-10 p-5 rounded-xl bg-[#080d17] border border-[#162238] flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-3">
          <div className="flex items-center space-x-3 text-center sm:text-left">
            <PhoneCall className="w-4 h-4 text-[#c59b27] shrink-0" />
            <span>
              {currentLang === 'en' 
                ? 'Need technical support or custom fee approval? Direct helpline:' 
                : currentLang === 'ru' 
                ? 'Нужна техническая помощь или подтверждение доступа? Прямая линия:' 
                : 'Texnik yordam yoki to‘lov tasdig‘i kerakmi? Ma’muriyat bilan to‘g‘ridan-to‘g‘ri aloqa:'}
              {' '}
              <a href="tel:+998973250770" className="font-mono font-bold text-slate-100 hover:text-[#c59b27] ml-1">
                +998 (97) 325-07-70
              </a>
            </span>
          </div>

          <a 
            href="https://t.me/sevinchadvokat" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[11px] font-semibold text-[#c59b27] hover:underline flex items-center"
          >
            <span>{currentLang === 'en' ? 'Telegram Support (@sevinchadvokat)' : currentLang === 'ru' ? 'Telegram (@sevinchadvokat)' : 'Telegram orqali yozish (@sevinchadvokat)'}</span>
            <ExternalLink className="w-3 h-3 ml-1" />
          </a>
        </div>

      </div>
    </section>
  );
}
